'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { useGlobalUI } from 'contexts/GlobalUIContext';
import ScienceBreadcrumb, {
  SCIENCE_ROOT_CRUMB,
} from '../studies/ScienceBreadcrumb';
import RichText, { ReferenceList } from '../studies/RichText';
import ASK_QUESTIONS, { ASK_CATEGORY_LABEL, type AskQuestion } from './askData';
import { CATEGORY_DOT } from './askTokens';
import type { AskArticleProps, AskFigureData } from './askArticles';
import AskFigure from './AskFigure';
import styles from './AskDetail.module.css';

type Block = { html: string } | { figureId: string };

/* Split on extractor-emitted markers so this can run on the server without a DOM. */
const toBlocks = (html: string): Block[] => {
  const out: Block[] = [];
  const re = /<div data-figure="([^"]+)"><\/div>/g;
  let last = 0;
  let m = re.exec(html);
  while (m) {
    if (m.index > last) out.push({ html: html.slice(last, m.index) });
    out.push({ figureId: m[1] });
    last = m.index + m[0].length;
    m = re.exec(html);
  }
  if (last < html.length) out.push({ html: html.slice(last) });
  return out;
};

/* The tail of every Ask body is, in order: the standing disclaimer
   (<p class="a-note">) and then a "Go deeper" heading with its links. Both
   are peeled off here so the page can close in the order a reader expects —
   article, sources, disclaimer, where to read next — rather than leaving the
   references stranded behind the disclaimer and the links. An article
   missing either part is unaffected. */
const GO_DEEPER = /<h2>\s*Go deeper\s*<\/h2>/i;
const DISCLAIMER = /<p class="a-note">/i;

const splitTail = (
  html: string,
): { bodyHtml: string; noteHtml: string; goDeeperHtml: string } => {
  const g = GO_DEEPER.exec(html);
  const goDeeperHtml = g ? html.slice(g.index) : '';
  const head = g ? html.slice(0, g.index) : html;

  const d = DISCLAIMER.exec(head);
  return {
    bodyHtml: d ? head.slice(0, d.index) : head,
    noteHtml: d ? head.slice(d.index) : '',
    goDeeperHtml,
  };
};

const AskDetail = ({
  article,
  question,
  figures,
  siblings,
}: {
  article: AskArticleProps;
  question: AskQuestion;
  figures: Record<string, AskFigureData>;
  /* Every question, for resolving the "Go deeper" links to a card. Passed in
     by the route so the titles follow the CMS; defaults to the vendored
     extract, which is what the links used to resolve against unconditionally
     — a question retitled in Anton kept its old title on every card that
     pointed at it. */
  siblings?: AskQuestion[];
}) => {
  /* Header stays put on scroll. Animating a fixed, backdrop-blurred layer
     forces the compositor to re-sample the backdrop on every direction change,
     which reads as the page flickering — the same reason the byte and accuracy
     detail pages pin it. Not redundant with the default: the studies hub sets
     headerCollaspable: true, so a client nav from the rail into a question
     would otherwise arrive with a collapsible header. */
  useGlobalUI({ headerCollaspable: false });

  const t = useTranslations('science');
  const primary = question.categories[0];
  /* "Go deeper" is authored as the last <h2> of bodyHtml, so it rendered
     between the article and its references and pushed the reference list
     away from the piece it belongs to (Nihav, 2026-09-04). Split it off the
     body here and render it AFTER the references: sources first, then where
     to read next. All 13 articles end with this exact heading followed by a
     single <ul class="a-links">, and an article without one is unaffected. */
  const { bodyHtml, noteHtml, goDeeperHtml } = useMemo(
    () => splitTail(article.bodyHtml),
    [article.bodyHtml],
  );
  /* "Go deeper" was authored as a bare <ul> of links. It recommends other
     questions, so it now renders the same card the hub uses rather than a
     list of underlined text. Resolved against ASK_QUESTIONS by slug: a link
     that no longer matches a question is dropped rather than rendered as a
     card with nothing in it. */
  const goDeeper = useMemo(() => {
    const slugs = Array.from(
      goDeeperHtml.matchAll(/href="\/science\/ask\/([a-z0-9-]+)\/?"/g),
    ).map((m) => m[1]);
    const seen = new Set<string>();
    return slugs
      .filter((slug) => (seen.has(slug) ? false : (seen.add(slug), true)))
      .filter((slug) => slug !== question.slug)
      .map((slug) => (siblings ?? ASK_QUESTIONS).find((q) => q.slug === slug))
      .filter((q): q is AskQuestion => Boolean(q));
  }, [goDeeperHtml, question.slug, siblings]);
  const blocks = useMemo(() => toBlocks(bodyHtml), [bodyHtml]);
  const figureMeta = useMemo(
    () => new Map(article.figures.map((f) => [f.id, f])),
    [article.figures],
  );

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.headingBlock}>
          <ScienceBreadcrumb
            dark
            crumbs={[
              SCIENCE_ROOT_CRUMB,
              {
                label: t('science.ask.heading'),
                href: '/science/studies#questions',
              },
              { label: question.shortLabel },
            ]}
          />

          <div className={styles.eyebrowRow}>
            <span className={styles.categoryPill}>
              <span
                className={styles.categorySwatch}
                style={{ background: CATEGORY_DOT[primary] }}
                aria-hidden="true"
              />
              <span className={styles.categoryLabel}>
                {t(`science.askCategory.${primary}`)}
              </span>
            </span>
            <span className={styles.eyebrowGroup}>
              {question.cohort ? (
                <span className={styles.eyebrow}>{question.cohort}</span>
              ) : null}
            </span>
          </div>

          <h1 className={styles.heading}>{article.title}</h1>
          {article.dek ? <p className={styles.deck}>{article.dek}</p> : null}

          <div className={styles.separator} role="presentation" />

          {article.stand ? (
            <p className={styles.stand}>{article.stand}</p>
          ) : null}
          {article.askNote ? (
            <p className={styles.askNote}>{article.askNote}</p>
          ) : null}

          <div className={styles.prose}>
            {blocks.map((b, i) =>
              'figureId' in b ? (
                <AskFigure
                  key={b.figureId}
                  meta={figureMeta.get(b.figureId)!}
                  data={figures[b.figureId]}
                  category={primary}
                />
              ) : (
                <RichText
                  key={`html-${i}`}
                  variant="dark"
                  html={b.html}
                  className={styles.proseBody}
                />
              ),
            )}
          </div>
        </div>

        {article.referencesHtml ? (
          /* No separator here: the "References" heading is authored at the end
             of bodyHtml and already carries the section rule above it, so a
             second line between the heading and the list was drawing twice. */
          <div className={styles.refContainer}>
            <ReferenceList
              $variant="dark"
              dangerouslySetInnerHTML={{ __html: article.referencesHtml }}
            />
          </div>
        ) : null}

        {noteHtml ? (
          <div className={styles.noteContainer}>
            <RichText
              variant="dark"
              html={noteHtml}
              className={styles.noteBody}
            />
          </div>
        ) : null}

        {goDeeper.length ? (
          <div className={styles.goDeeperContainer}>
            <div className={styles.separator} role="presentation" />
            <h2 className={styles.goDeeperHeading}>
              {t('science.ask.goDeeper')}
            </h2>
            <div className={styles.relList}>
              {goDeeper.map((q) => (
                <Link
                  className={styles.relCard}
                  key={q.slug}
                  href={`/science/ask/${q.slug}`}
                  aria-label={q.question}
                >
                  {q.coverImageUrl ? (
                    <span className={styles.relImg} aria-hidden="true">
                      <img
                        className={styles.relImgEl}
                        src={q.coverImageUrl}
                        alt=""
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                  ) : null}
                  <span className={styles.relBody}>
                    <span className={styles.relCat}>
                      {ASK_CATEGORY_LABEL[q.categories[0]]}
                    </span>
                    <span className={styles.relTitle}>{q.question}</span>
                  </span>
                  <span className={styles.relArrow} aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 10 10" fill="none">
                      <path
                        fill="currentColor"
                        d="M7.703 5.8H.398V4.6h7.305l-3.36-3.36.855-.84 4.8 4.8-4.8 4.8-.855-.84 3.36-3.36Z"
                      />
                    </svg>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default AskDetail;
